/**
 * DEBUG FLAG — set to `true` to show the storage upgrade banner for ALL users
 * regardless of their plan (useful for testing placement & design).
 * Set back to `false` before releasing to production.
 */
export const SHOW_STORAGE_BANNER_FOR_ALL = false;

/**
 * Average sizes used for storage estimate calculations.
 * - Photo: ~5 MB  (typical compressed JPEG from a smartphone)
 * - Video: ~85 MB per minute  (≈ 1080p / 720p mix, ~8–12 Mbps bitrate)
 */
const AVG_PHOTO_BYTES = 5 * 1024 * 1024;      // 5 MB
const AVG_VIDEO_MIN_BYTES = 85 * 1024 * 1024; // 85 MB per minute

export interface StorageEstimate {
  photos: number;
  videoMins: number;
}

/**
 * Calculates how many photos and minutes of video can approximately
 * be stored in the given number of available bytes.
 *
 * @param availableBytes - Remaining free storage in bytes
 * @returns Approximate number of photos and video minutes
 */
export function calcStorageEstimate(availableBytes: number): StorageEstimate {
  const safe = Math.max(0, availableBytes);
  const photos = Math.floor(safe / AVG_PHOTO_BYTES);
  const videoMins = Math.floor(safe / AVG_VIDEO_MIN_BYTES);
  return {
    photos: roundToNearest(photos),
    videoMins: roundToNearest(videoMins),
  };
}

/** Rounds a number to the nearest "friendly" magnitude (5, 10, 50, 100, 500, 1000…). */
function roundToNearest(n: number): number {
  if (n <= 0) return 0;
  const magnitude = Math.pow(10, Math.floor(Math.log10(n)));
  const step = magnitude >= 10 ? magnitude / 2 : magnitude;
  return Math.round(n / step) * step;
}

/**
 * Formats a byte count to a human-readable GB string (2 decimal places).
 * e.g. 322122547 → "0.30"
 */
export function bytesToGBString(bytes: number): string {
  return (bytes / (1024 * 1024 * 1024)).toFixed(2);
}
